<template>
	<div class="container">
		<div class="page">
			<!-- Header -->
			<div class="page-header">
				<div class="row">
					<div class="col">
						<h1 class="page-title">{{ $t('Fraud Monitoring') }}</h1>
					</div>
					<div class="col-sm-auto col-xs-12 d-flex justify-content-end">
						<!-- Date Filter -->
						<daterangepicker
							class="mt-2"
							:presets="filterDatePresets"
							:start="since"
							:end="until"
							@selected="setFilterDate"
						>
						</daterangepicker>
					</div>
				</div>
			</div>

			<!-- Overviews -->
			<div class="fraud-monitoring-scoreboard">
				<div class="row" v-if="overview">
					<div class="col-md-3 col-6" v-for="(item, idx) in overview" :key="idx">
						<overview
							:text="item.label"
							:type="'normal'"
							:with-additional="false"
							:number="item.value"
							:clickable="true"
							:class="{ 'active': status === item.key }"
							:is-fetching="isFetchingStatistic"
							@click="overviewAction(item.key)">
						</overview>
					</div>
				</div>
			</div>

			<!-- Table Menu -->
			<div class="table-list-menu d-lg-flex align-items-center justify-content-between">

				<div class="table-actions d-flex align-items-center">
					<!-- Add Seller Fraud -->
					<button
						type="button"
						class="btn btn-primary btn-sm flex-center mr-2 mb-0"
						@click="toggleModalFraud(true, 'Add')"
					>
						<i class="material-icons">add</i>
						<span class="no-wrap">{{ $t('Seller Fraud') }}</span>
					</button>

					<!-- Search Keyword -->
					<input
						type="text"
						class="input form-control form-control-sm products-search-box"
						:placeholder="`${$t('Search')}..`"
						@input="onKeywordChange"
						v-model="keyword"
					>
				</div>

				<div class="table-filters text-right d-flex align-items-center justify-content-end">
					<!-- Clear Filter -->
					<div class="d-flex align-items-center">
						<button
							type="button"
							class="btn btn-plain btn-sm"
							@click="clearFilter"
							v-if="isFilterOn"
						>
							{{ $t('Clear Filter') }}
						</button>
					</div>

					<!-- Export -->
					<div class="d-flex align-items-center ml-2">
						<button
							type="button"
							class="btn btn-sm btn-primary"
							:class="{ 'is-loading': isExporting }"
							@click="exportFraud"
						>
							<i class="material-icons">save_alt</i>
							<span class="mr-2">{{ $t('Export') }}</span>
						</button>
					</div>
				</div>
			</div>

			<div class="products-list table-responsive">
				<table class="products-list-table table table-primary table-striped mb-0">
					<thead>
						<tr class="text-left">
							<td width="5%">{{ $t('No.') }}</td>
							<td class="th-sort" :class="sortClass('name')" @click="changeSorting('name')">{{ $t('Seller Data') }}</td>
							<td class="th-sort" :class="sortClass('email')" @click="changeSorting('email')">{{ $t('Contact') }}</td>
							<td class="th-sort" :class="sortClass('fraud.status')" @click="changeSorting('fraud.status')">{{ $t('Status') }}</td>
							<td class="th-sort" :class="sortClass('fraud.info')" @click="changeSorting('fraud.info')">{{ $t('Informasi') }}</td>
							<td class="th-sort mw-170" :class="sortClass('fraud.last_checked')" @click="changeSorting('fraud.last_checked')">{{ $t('Last Checked') }}</td>
							<td>{{ $t('Action') }}</td>
						</tr>
					</thead>
					<tbody v-if="fraudUsers.length > 0">
						<tr v-for="(item, idx) in fraudUsers" :key="idx">
							<td>{{ idx + 1 }}</td>
							<td class="mw-150">
								{{ item.name }}
								<br />
								{{ item.store_name }}
							</td>
							<td class="mw-150">
								{{ item.email }}
								<br />
								{{ item.phone }}
							</td>
							<td>
								<span :class="`badge badge-${formatStatus(item.fraud.status).color}`">
									{{ formatStatus(item.fraud.status).label }}
								</span>
							</td>
							<td>
								{{ formatInfo(item) }}
							</td>
							<td class="mw-170">{{ formatDate(item.fraud.last_checked) }}</td>
							<td class="d-flex flex-nowrap text-primary">
								<i
									v-tooltip="'Edit'"
									class="bi bi-pencil-square action-icon"
									@click="toggleModalFraud(true, 'Edit', item)"
								></i>
							</td>
						</tr>
					</tbody>
				</table>

				<div class="list-empty table-empty" v-if="fraudUsers.length === 0 && isFetching === false">
					<i class="material-icons mr-2 va-m">error_outline</i>
					<span class="va-m">{{ $t('No fraud data was found') }}</span>
				</div>
			</div>

			<!-- Table Loader -->
			<div class="table-fetch-status" v-if="isFetching">
				<div class="is-loading large dark m-auto"></div>
			</div>

			<!-- Modals -->
			<div class="modals">

				<modal
					class="table-filter-modal"
					size="medium"
					@close="toggleModalFraud(false)"
					v-if="isShowModalFraud"
				>
					<div slot="header">
						<h3>{{ modalMode }} Seller Fraud</h3>
					</div>
					<div slot="body">
						<div class="row">

							<!-- Seller -->
							<div class="col-md-12">
								<div class="form-group">
									<div class="mb-2">
										<label>Seller <span class="text-danger">*</span></label>
										<multiselect
											v-model="selectedSeller"
											:show-labels="false"
											track-by="_id"
											label="label"
											placeholder="email/nama seller"
											:options="optionSeller"
											:loading="isSearchingSeller"
											:disabled="modalMode === 'Edit'"
											@search-change="fetchDataSeller"
											@select="onSelectSeller"
										>
											<span slot="noResult">
												No options matching the query
											</span>
											<span slot="noOptions">
												No options available
											</span>
										</multiselect>
										<small v-if="errorMessages.selectedSeller" class="text-danger">
											{{ errorMessages.selectedSeller }}
										</small>
									</div>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<div class="mb-2">
										<label>Tipe Fraud <span class="text-danger">*</span></label>
										<select
											v-model="selectedType"
											class="form-control"
										>
											<option
												v-for="fraudType in fraudTypeOptions"
												:value="fraudType.value"
												:key="fraudType.value"
											>
												{{ fraudType.label }}
											</option>
										</select>
										<small v-if="errorMessages.selectedType" class="text-danger">
											{{ errorMessages.selectedType }}
										</small>
									</div>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<div class="mb-2">
										<label>Status Fraud <span class="text-danger">*</span></label>
										<select
											v-model="selectedStatus"
											class="form-control"
										>
											<option
												v-for="status in fraudStatusOptions"
												:value="status.value"
												:key="status.value"
											>
												{{ status.label }}
											</option>
										</select>
										<small v-if="errorMessages.selectedStatus" class="text-danger">
											{{ errorMessages.selectedStatus }}
										</small>
									</div>
								</div>
							</div>

							<div class="col-md-12">
								<div class="form-group">
									<div class="mb-2">
										<label>Informasi</label>
										<textarea
											class="form-control"
											placeholder="Masukkan Informasi Fraud..."
											rows="3"
											v-model="selectedInfo"
										></textarea>
									</div>
								</div>
							</div>

						</div>
					</div>

					<div slot="footer">
						<button
							type="button"
							class="btn btn-light"
							@click="toggleModalFraud(false)"
						>
							{{ $t('Close') }}
						</button>
						<button
							type="button"
							class="btn btn-primary"
							:class="{ 'is-loading': isSubmitFraud }"
							:disabled="isDisabled"
							@click="submitFraud"
						>
							Update
						</button>
					</div>
				</modal>

			</div>

		</div>
		<notifications group="product-requests" position="bottom right" />
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import adminApi, { getFraudUsers, exportFraud, updateFraudUser } from '@/api/admin';
import statsApi from '@/api/statistic';
import Daterangepicker from '@/components/Daterangepicker.vue';
import Modal from '@/components/Modal.vue';
import Overview from '@/components/Overview.vue';
import {
	getDateRange,
	duplicateVar,
	getAxiosErrorMessage,
	downloadFile,
} from '@/lib/helper';
import {
	fraudStatusOptions,
	fraudTypeOptions,
} from '@/lib/statics';

const defaultFilterDate = getDateRange();

export default {
	name: 'AdminFraudMonitoring',
	components: {
		Daterangepicker,
		Modal,
		Overview,
	},
	data() {
		return {
			page: 1,
			keyword: '',
			limit: 50,
			sort_by: 'fraud.last_checked',
			sort: 'desc',
			status: 'all',
			filterDate: defaultFilterDate,
			isAll: false,
			isFetching: false,
			fraudUsers: [],
			errorMessages: {
				selectedSeller: '',
				selectedType: '',
				selectedStatus: '',
			},
			isFetchingStatistic: false,
			overview: {
				not_fraud_count: {
					key: 'not_fraud',
					label: 'Not Fraud',
					value: 0,
				},
				potential_fraud_count: {
					key: 'potential_fraud',
					label: 'Potential Fraud',
					value: 0,
				},
				fraud_count: {
					key: 'fraud',
					label: 'Fraud',
					value: 0,
				},
				total_count: {
					key: 'all',
					label: 'Total',
					value: 0,
				},
			},
			fraudStatusOptions,
			fraudTypeOptions,
			modalMode: 'Add',
			isShowModalFraud: false,
			selectedSeller: null,
			selectedType: 'receipt_number_50',
			selectedStatus: 'not_fraud',
			selectedInfo: '',
			optionSeller: [],
			isSearchingSeller: false,
			isSubmitFraud: false,
			isExporting: false,
		};
	},
	methods: {
		handleScroll() {
			const pageHeight = document.documentElement.offsetHeight;
			const windowHeight = window.innerHeight;
			const scrollPosition = window.scrollY;
			const offset = 50;
			const isOnBottom = pageHeight - offset <= windowHeight + scrollPosition;
			if (isOnBottom) {
				if (!this.isFetching && !this.isAll) {
					this.fetchFraudUsers(false);
				}
			}
		},
		fetchFraudUsers(reset = true) {
			if (reset) {
				this.resetFraudUsers();
			}

			this.isFetching = true;
			const params = this.params;

			const callback = (response) => {
				const fraudUsers = response.data?.users ?? [];
				this.fraudUsers = this.fraudUsers.concat(fraudUsers);
				this.isAll = fraudUsers.length < this.limit;
				this.page++;
				this.isFetching = false;
			};
			const errorCallback = (e) => {
				const msg = getAxiosErrorMessage(e);
				// eslint-disable-next-line
				console.log(msg);
			};
			getFraudUsers(params, callback, errorCallback);
		},
		resetFraudUsers() {
			this.fraudUsers = [];
			this.isAll = false;
			this.page = 1;
		},
		formatDate(dateString) {
			const dateNumberLong = date => new Date(parseInt(date.$date.$numberLong));
			const date = typeof dateString === 'object' ? dateNumberLong(dateString) : dateString;
			const timezone = this.user ? this.user.timezone : 'Asia/Jakarta';
			const formattedDate = this.moment
				.utc(date)
				.tz(timezone)
				.format('DD-MM-YYYY - HH:mm');
			return dateString && dateString !== '' ? formattedDate : '-';
		},
		formatStatus(statusValue) {
			const selectedStatus = this.fraudStatusOptions.find(status => status.value === statusValue);
			return {
				label: selectedStatus?.label,
				color: selectedStatus?.color,
			};
		},
		formatInfo(item) {
			return item?.fraud?.info ?? '';
		},
		onKeywordChange() {
			const self = this;
			this.ooDelay(() => {
				self.fetchFraudUsers();
			}, 1000);
		},
		clearFilter() {
			window.location.href = '/admin/fraud-monitoring';
		},
		initFilterDate() {
			if (this.since && this.since !== '' && this.until && this.until !== '') {
				const since = this.moment(this.since);
				const until = this.moment(this.until);
				const filterDate = {
					start: since,
					end: until,
				};
				this.filterDate = filterDate;
			}
		},
		setFilterDate(filterDate) {
			this.filterDate = filterDate;
			const format = 'YYYY-MM-DD';
			const since = this.moment(filterDate.start).format(format);
			const until = this.moment(filterDate.end).format(format);
			const query = duplicateVar(this.$route.query);
			query.since = since;
			query.until = until;
			const params = { query };
			this.$router.push(params);
			this.fetchFraudUsers();
			this.fetchStats();
		},
		exportFraud() {
			this.isExporting = true;

			const params = this.params;

			const callback = (response) => {
				const url = response.data;
				this.isExporting = false;
				downloadFile(url);
			};

			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					title: 'Export Fraud',
					text: message,
					type: 'error',
				});
				this.isExporting = false;
			};

			exportFraud(params, callback, errorCallback);
		},
		sellerValidation() {
			this.errorMessages.selectedSeller = '';
			if (!this.selectedSeller) {
				this.errorMessages.selectedSeller = 'Seller Wajib Dipilih';
			}
		},
		typeValidation() {
			this.errorMessages.selectedType = '';
			if (!this.selectedType) {
				this.errorMessages.selectedType = 'Tipe Wajib Dipilih';
			}
		},
		statusValidation() {
			this.errorMessages.selectedStatus = '';
			if (!this.selectedType) {
				this.errorMessages.selectedStatus = 'Status Wajib Dipilih';
			}
		},
		formValidation() {
			this.sellerValidation();
			this.typeValidation();
			this.statusValidation();
		},
		submitFraud() {
			this.formValidation();
			const isErrorExist = Object.values(this.errorMessages).find(val => val !== '');
			if (isErrorExist) {
				this.$notify({
					group: 'app',
					type: 'error',
					title: 'Failed',
					text: 'Form harus diisi.',
				});
				return;
			}

			this.isSubmitFraud = true;

			const selectedSellerId = this.selectedSeller?._id?.$oid ?? this.selectedSeller?._id;

			const params = {
				status: this.selectedStatus,
				type: this.selectedType,
				info: this.selectedInfo,
			};

			const callback = (response) => {
				const message = response.message;
				this.$notify({
					group: 'app',
					title: 'Update Request Product',
					text: message,
					type: 'success',
				});
				this.isSubmitFraud = false;
				this.isShowModalFraud = false;
				this.fetchFraudUsers();
				this.fetchStats();
			};

			const errorCallback = (e) => {
				const message = getAxiosErrorMessage(e);
				this.$notify({
					group: 'app',
					title: 'Submit Fraud',
					text: message,
					type: 'error',
				});
				this.isSubmitFraud = false;
			};

			updateFraudUser(selectedSellerId, params, callback, errorCallback);
		},
		fetchStats() {
			this.isFetchingStatistic = true;

			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const params = {
				items: 'fraud_users',
				since: startDate,
				until: endDate,
			};

			const callback = (response) => {
				const stats = response.data?.fraud_users;

				if (stats) {
					Object.keys(stats).forEach((key) => {
						const value = stats[key] ?? 0;
						this.overview[key].value = value;
					});
				}

				this.isFetchingStatistic = false;
			};

			const errorCallback = (e) => {
				console.log(e);

				this.isFetchingStatistic = false;
			};

			statsApi.getAppStatsNew(params, callback, errorCallback);
		},
		overviewAction(key) {
			this.status = key;
			this.fetchFraudUsers();
		},
		toggleModalFraud(state = true, mode = 'Add', seller = null) {
			this.optionSeller = [];
			this.modalMode = mode;
			this.isShowModalFraud = state;

			if (mode === 'Edit' && seller) {
				const sellerName = seller?.name ?? '';
				const sellerEmail = seller?.email ?? '';
				const sellerId = seller?._id?.$oid ?? seller?._id;
				const sellerItem = {
					_id: sellerId,
					name: sellerName,
					email: sellerEmail,
					label: `${sellerName} - ${sellerEmail}`,
				};
				this.optionSeller.push(sellerItem);
				this.selectedSeller = sellerItem;
				this.selectedInfo = seller?.fraud?.info ?? '';
				this.selectedType = seller?.fraud?.type ?? 'receipt_number_50';
				this.selectedStatus = seller?.fraud?.status ?? 'not_fraud';
			} else {
				this.selectedSeller = null;
				this.selectedInfo = '';
				this.selectedType = 'receipt_number_50';
				this.selectedStatus = 'not_fraud';
			}
		},
		fetchDataSeller(query) {
			this.isSearchingSeller = true;

			const params = {
				keyword: query,
				role: 'owner',
				owner_type: 'dropshipper',
			};

			if (this.modalMode === 'Add') {
				params.fraud_exists = false;
			}

			const callback = (response) => {
				this.optionSeller = response.data;
				this.isSearchingSeller = false;
			};
			const errorCallback = () => {
				this.isSearchingSeller = false;
			};

			this.ooDelay(() => {
				if (query !== '') {
					adminApi.searchUser(params, callback, errorCallback);
				} else {
					this.isSearchingSeller = false;
				}
			}, 1000);
		},
		onSelectSeller() {
			this.isSearchingSeller = false;
		},
		changeSorting(key) {
			const isSame = this.sort_by === key;
			this.sort_by = key;
			const sort = isSame && this.sort === 'asc' ? 'desc' : 'asc';
			this.sort = sort;
			// Re-fetch
			this.fetchFraudUsers(true);
		},
		sortClass(key) {
			const sameKey = this.sort_by === key;
			const className = sameKey ? `th-sort-${this.sort}` : '';
			return className;
		},
	},
	computed: {
		...mapGetters({
			user: 'user',
			filterDatePresets: 'filterDatePresets',
			currency: 'currency',
		}),
		since() {
			const since = this.$route.query.since;
			return since;
		},
		until() {
			const until = this.$route.query.until;
			return until;
		},
		params() {
			const dateFormat = 'YYYY-MM-DD';
			const startDate = this.moment(this.filterDate.start).format(dateFormat);
			const endDate = this.moment(this.filterDate.end).format(dateFormat);

			const params = {
				limit: this.limit,
				sort_by: this.sort_by,
				sort: this.sort,
				page: this.page,
				since: startDate,
				until: endDate,
			};

			if (this.keyword && this.keyword !== '') {
				params.keyword = this.keyword;
			}

			if (this.status !== 'all') {
				params.status = this.status;
			}

			return params;
		},
		isFilterOn() {
			return (
				this.since
				|| this.until
				|| this.keyword.length > 0
			);
		},
		isDisabled() {
			return !this.selectedSeller || !this.selectedType || !this.selectedStatus;
		},
	},
	created() {
		this.initFilterDate();
		this.fetchFraudUsers();
		this.fetchStats();
		// On Scroll Bottom
		window.addEventListener('scroll', this.handleScroll);
	},
	destroyed() {
		window.removeEventListener('scroll', this.handleScroll);
	},
};
</script>

<style scoped>
.action-icon {
	font-size: 24px;
	cursor: pointer;
	margin-left: 0.5rem !important;
	margin-right: 0.5rem !important;
}

.no-wrap {
	white-space: nowrap;
}

.mw-150 {
	min-width: 150px;
}

.mw-170 {
	min-width: 170px;
}
</style>
